import { Item } from "@thenounproject/lingo-core";

type CanvasLayoutProperties = {
  size: number;
  isSquare: boolean;
  outerWidth: string;
  innerThumbnailWidth: string;
};

const GAP = 16;

const CONTAINER_PADDING = 80;
const MIN_ITEM_WIDTH_SQUARE = 100;
const MIN_ITEM_WIDTH_ROW = 60;
const MIN_CAPTION_WIDTH = 180;

// Container-based breakpoints (not viewport) for responsive sizing
const CONTAINER_BREAKPOINTS = {
  XL: 800,
  L: 700,
  M: 600,
  S: 500,
} as const;

// SIZE_MAP[breakpoint][originalSize-1] => effectiveSize
// Maps display sizes (1-6) to responsive sizes based on container width
// Square layouts need more aggressive reduction to maintain readable grid items
const SIZE_MAP_SQUARE: Record<string, number[]> = {
  XL: [1, 2, 3, 4, 5, 6],
  L: [1, 2, 3, 4, 4, 5],
  M: [1, 2, 2, 3, 3, 4],
  S: [1, 1, 2, 2, 2, 3],
  XS: [1, 1, 2, 2, 3, 3],
};

// Row layouts can maintain smaller sizes since they take full width
const SIZE_MAP_ROW: Record<string, number[]> = {
  XL: [1, 2, 3, 4, 5, 6],
  L: [1, 2, 3, 4, 5, 6],
  M: [1, 2, 3, 4, 4, 5],
  S: [1, 2, 2, 3, 3, 4],
  XS: [1, 1, 2, 3, 3, 4],
};

type LayoutType = "square" | "row";

function getResponsiveSize(
  rawSize: number,
  containerWidth: number | null,
  layoutType: LayoutType
): number {
  if (!containerWidth) return rawSize;

  let breakpoint: string;
  if (containerWidth >= CONTAINER_BREAKPOINTS.XL) breakpoint = "XL";
  else if (containerWidth >= CONTAINER_BREAKPOINTS.L) breakpoint = "L";
  else if (containerWidth >= CONTAINER_BREAKPOINTS.M) breakpoint = "M";
  else if (containerWidth >= CONTAINER_BREAKPOINTS.S) breakpoint = "S";
  else breakpoint = "XS";

  const sizeMap = layoutType === "row" ? SIZE_MAP_ROW : SIZE_MAP_SQUARE;
  const sizeIndex = Math.min(Math.max(rawSize, 1), 6) - 1;
  return sizeMap[breakpoint][sizeIndex];
}

export const getCanvasLayoutProperties = ({
  item,
  containerWidth,
}: {
  item: Item;
  containerWidth?: number;
}): CanvasLayoutProperties => {
  const rawSize = item?.displayProperties?.size ?? 1;
  const imageAlignment = item?.displayProperties?.imageAlignment;

  // Determine layout type early for responsive sizing
  const isRowLayout =
    ["leading", "trailing"].includes(imageAlignment) || item?.data?.displayStyle === "full_width";
  const layoutType: LayoutType = isRowLayout ? "row" : "square";

  // Step 1: Map to responsive size based on container width and layout type
  const responsiveSize = getResponsiveSize(rawSize, containerWidth ?? null, layoutType);

  // Step 2: Apply min-width cap as safety net
  const minWidth = layoutType === "row" ? MIN_ITEM_WIDTH_ROW : MIN_ITEM_WIDTH_SQUARE;
  const usableWidth = containerWidth ? containerWidth - CONTAINER_PADDING : null;
  const maxThatFit = usableWidth
    ? Math.max(1, Math.floor((usableWidth + GAP) / (minWidth + GAP)))
    : responsiveSize;
  const size = Math.min(responsiveSize, maxThatFit);

  // isSquare is true if the image is above, we also force the image above if size === 1.
  const isSquare = imageAlignment === "top" || size === 1;
  const isFullWidth =
    ["leading", "trailing"].includes(imageAlignment) || item?.data?.displayStyle === "full_width";

  const outerWidth =
    isSquare || !isFullWidth ? `calc((100% - ${(size - 1) * GAP}px) / ${size})` : "100%";
  const multiplier = size > 2 ? 1 + (size - 2) * 0.075 : 1;
  const innerThumbnailWidth = `${100 / (size * multiplier)}%`;

  // Calculate if the caption would be too narrow in a non-square layout
  // If so, force square layout to give caption more room

  let forceSquareForCaption = false;
  if (item.displayProperties?.caption && usableWidth && !isSquare) {
    const thumbnailPercent = 100 / (size * multiplier);
    const captionWidth = usableWidth * (1 - thumbnailPercent / 100);
    forceSquareForCaption = captionWidth < MIN_CAPTION_WIDTH;
  }

  return {
    size,
    isSquare: isSquare || forceSquareForCaption,
    outerWidth,
    innerThumbnailWidth,
  };
};
